'use client';

import type { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';
import { useCallback, useEffect, useRef, useState } from 'react';

let enginePromise: Promise<typeof import('embla-carousel')> | undefined;

function loadEngine() {
  // One shared async chunk, even when several product rows enter the viewport together.
  enginePromise ??= import('embla-carousel').catch(error => {
    enginePromise = undefined;
    throw error;
  });
  return enginePromise;
}

function sameOptions(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  // DOM nodes and callbacks in custom Embla options must retain identity semantics.
  if (
    Object.getPrototypeOf(a) !== Object.prototype ||
    Object.getPrototypeOf(b) !== Object.prototype
  ) {
    return false;
  }
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);
  return (
    keys.length === Object.keys(right).length &&
    keys.every(
      key => Object.prototype.hasOwnProperty.call(right, key) && sameOptions(left[key], right[key]),
    )
  );
}

/** Enhance the existing Saprem SSR slides; never replace their HTML with a loading fallback. */
export function useDeferredCarousel(options: EmblaOptionsType, imageReady: boolean) {
  const [viewport, attach] = useState<HTMLDivElement | null>(null);
  const [requested, setRequested] = useState(false);
  const [interacted, setInteracted] = useState(false);
  const [visible, setVisible] = useState(false);
  const [api, setApi] = useState<EmblaCarouselType>();
  const apiRef = useRef<EmblaCarouselType | undefined>(undefined);
  const optionsRef = useRef(options);
  const pendingAction = useRef<((carousel: EmblaCarouselType) => void) | undefined>(undefined);

  const activate = useCallback(() => {
    setInteracted(true);
    setRequested(true);
  }, []);

  const run = useCallback(
    (action: (carousel: EmblaCarouselType) => void) => {
      if (apiRef.current) action(apiRef.current);
      else {
        pendingAction.current = action;
        activate();
      }
    },
    [activate],
  );

  useEffect(() => {
    if (!viewport) return;
    if (typeof IntersectionObserver === 'undefined') {
      setRequested(true);
      setVisible(true);
      return;
    }
    const nearby = new IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        setRequested(true);
        nearby.disconnect();
      },
      { rootMargin: '200px 0px' },
    );
    const onscreen = new IntersectionObserver(entries => {
      setVisible(entries.some(entry => entry.isIntersecting));
    });
    nearby.observe(viewport);
    onscreen.observe(viewport);
    return () => {
      nearby.disconnect();
      onscreen.disconnect();
    };
  }, [viewport]);

  useEffect(() => {
    if (sameOptions(optionsRef.current, options)) return;
    optionsRef.current = options;
    apiRef.current?.reInit(options);
  }, [options]);

  const shouldInitialize = requested && (imageReady || interacted);

  useEffect(() => {
    // A remounted viewport must not expose a destroyed API while its replacement loads.
    setApi(undefined);
    if (!viewport || !shouldInitialize) return;
    let cancelled = false;
    let instance: EmblaCarouselType | undefined;
    loadEngine()
      .then(({ default: EmblaCarousel }) => {
        if (cancelled) return;
        instance = EmblaCarousel(viewport, optionsRef.current);
        apiRef.current = instance;
        setApi(instance);
        pendingAction.current?.(instance);
        pendingAction.current = undefined;
      })
      .catch(() => {
        // Preserve the SSR content on a transient chunk failure and allow another interaction to retry.
        if (!cancelled) setRequested(false);
      });
    return () => {
      cancelled = true;
      instance?.destroy();
      if (apiRef.current === instance) apiRef.current = undefined;
    };
  }, [viewport, shouldInitialize]);

  return { attach, api, activate, run, visible };
}
